import {
  Flex,
  Table,
  type TableColumnType,
  TablePageSizeSelector,
  Text,
  theme,
} from '@/components';
import { BigCalculator, formatValueByUnit } from '@/utils';
import type { SortOrder } from 'antd/es/table/interface';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import { useAnalysisPageSearchQueryStates } from '../../_hooks/search-query-states';
import type { ProjectAnalysisModels } from '../../_models/project';
import { getTableDataSource } from '../../_utils/get-table-datasource';
import { AnalysisDrillThroughDropdown } from './AnalysisDrillThroughDropdown';

type Props = {
  data: z.infer<typeof ProjectAnalysisModels>;
};

export const AnalysisResultTable: FC<Props> = ({ data }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  const {
    searchQueryStates: { page, pageSize, order, analysisAxis },
    setSearchQueryStates,
  } = useAnalysisPageSearchQueryStates();
  const fiscalYears = [...new Set(data.map((item) => item.fiscalYear))].sort();

  const tableDataSource = useMemo(() => {
    return getTableDataSource({ data, analysisAxis });
  }, [data, analysisAxis]);

  return (
    <>
      <Flex justify="flex-end" gap={16} style={{ marginBottom: 16 }}>
        <TablePageSizeSelector
          defaultPageSize={pageSize}
          onChange={(pageSize) => {
            setSearchQueryStates((prev) => ({
              ...prev,
              page: 1,
              pageSize,
            }));
          }}
        />
        <AnalysisDrillThroughDropdown fiscalYears={fiscalYears} />
      </Flex>
      <Table
        rowKey={(data) => data.id}
        columns={[
          {
            title: t('thousand-yen-unit-label'),
            key: 'name',
            width: 400,
            render: (_, record) => (
              <Text maxWidth={400} text={record.organizationName} />
            ),
          },
          ...(fiscalYears.map((fiscalYear) => ({
            title: `${fiscalYear}`,
            dataIndex: fiscalYear,
            key: fiscalYear,
            width: 250,
            align: 'right',
            defaultSortOrder: order?.includes(`${fiscalYear}`)
              ? order.includes('ascend' satisfies SortOrder)
                ? 'ascend'
                : 'descend'
              : undefined,
            sorter: (a, b) => {
              const aTotal =
                a.fiscalYears.find((item) => item.year === fiscalYear)?.total ??
                0;
              const bTotal =
                b.fiscalYears.find((item) => item.year === fiscalYear)?.total ??
                0;
              return aTotal - bTotal;
            },
            render: (_, record) => {
              const total = record.fiscalYears.find(
                (item) => item.year === fiscalYear,
              )?.total;
              return renderText(total);
            },
          })) satisfies Array<
            TableColumnType<(typeof tableDataSource)[number]>
          >),
        ]}
        dataSource={tableDataSource}
        pagination={{
          current: page,
          pageSize,
          showSizeChanger: false,
          total: tableDataSource.length,
          onChange: (page) => {
            setSearchQueryStates((prev) => ({ ...prev, page }));
          },
        }}
        onChange={(_, __, sorter) => {
          invariant(!Array.isArray(sorter), t('unreachable-code'));
          if (sorter.order && sorter.columnKey) {
            setSearchQueryStates((prev) => ({
              ...prev,
              order: `${sorter.order}_${sorter.columnKey}`,
            }));
          } else {
            setSearchQueryStates((prev) => ({
              ...prev,
              order: null,
            }));
          }
        }}
        summary={
          analysisAxis === 'year'
            ? undefined
            : () => (
                <Table.Summary.Row
                  style={{
                    background: token.colorBgLayout,
                    fontWeight: 600,
                  }}
                >
                  <Table.Summary.Cell index={0}>
                    {t('total')}
                  </Table.Summary.Cell>
                  {fiscalYears.map((year, index) => {
                    const total = tableDataSource.reduce((acc, curr) => {
                      return BigCalculator.add(
                        acc,
                        curr.fiscalYears.find((item) => item.year === year)
                          ?.total ?? 0,
                      );
                    }, 0);
                    return (
                      <Table.Summary.Cell
                        key={year}
                        index={index}
                        align="right"
                      >
                        {renderText(total)}
                      </Table.Summary.Cell>
                    );
                  })}
                </Table.Summary.Row>
              )
        }
      />
    </>
  );
};

const renderText = (value: number | undefined | null) => {
  if (value === undefined || value === null) {
    return '';
  }
  return formatValueByUnit(value, {
    operation: 'round',
    unit: 'thousand',
  });
};
